/**
 * React Query hook for managing scenario management data
 * This hook provides a clean interface for fetching, caching, and managing
 * scenario management entries using React Query.
 */

import useApiClients from '@hooks/useApiClients.tsx';
import { ScenarioManagementEntry } from '@services/api/deckManagement/graphql/schema/API';
import { getAllScenarioManagementEntriesForaDeck } from '@services/api/deckManagement/graphql/schema/queries';
import { useQuery } from '@tanstack/react-query';
import { createQueryOrMutationKey, createStandardQueryOptions, StandardQueryOptions } from '@utils/reactQueryConfig.ts';

// Query keys for React Query caching
export const scenarioQueryKeys = {
  all: createQueryOrMutationKey('scenario'),
  decks: () => createQueryOrMutationKey(...scenarioQueryKeys.all, 'decks'),
  deck: (deckId: string) => createQueryOrMutationKey(...scenarioQueryKeys.decks(), deckId),
  entries: (deckId: string) => createQueryOrMutationKey(...scenarioQueryKeys.deck(deckId), 'entries'),
};

/**
 * Hook for fetching all scenario management entries for a specific deck
 * @param deckId - The unique identifier for the deck
 * @param options - Additional React Query options
 * @returns Query result with scenario management entries data
 */
export const useScenarioManagementEntries = (deckId: string | null, options?: StandardQueryOptions) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: scenarioQueryKeys.entries(deckId || ''),
    queryFn: async (): Promise<ScenarioManagementEntry[]> => {
      if (!deckId) {
        throw new Error('Deck ID is required');
      }

      // Call the GraphQL API using the imported query
      const response = await deckManagementClient.graphql({
        query: getAllScenarioManagementEntriesForaDeck,
        variables: {
          deck_id: deckId,
        },
      });

      if ('data' in response && response.data?.getAllScenarioManagementEntriesForaDeck) {
        // Filter out null entries that might be returned from GraphQL
        return response.data.getAllScenarioManagementEntriesForaDeck.filter(
          (entry): entry is ScenarioManagementEntry => entry !== null,
        );
      }

      return [];
    },
    ...createStandardQueryOptions<ScenarioManagementEntry[]>(options),
  });
};
